import type { Line } from 'd3-shape';
import { line } from 'd3-shape';
import { select, type Selection } from 'd3-selection';
import { isNil, last, matchesProperty, remove } from 'lodash-es';
import type { Subscription } from 'rxjs';
import { combineLatest, map, Observable, startWith, Subject } from 'rxjs';
import type { Instance as TippyInstance } from 'tippy.js';
import tippy from 'tippy.js';
import { collectionNotIncludes } from '../utils/regex';
import { dustGraphDataTypesWithoutPM10, getDataType } from './data-types';
import { getMinuteDays, scrollTrigger } from './graph-helpers';
import type { ValueTimeGraph } from './graph';
import type { ValueTimeGraphZoomHelper } from './zoom-helper';

interface LegendItemOptions {
    type: string;
    title?: string;
    clickable?: boolean;
    hidden?: boolean;
    available?: boolean;
    header?: boolean;
    information?: string;
    line?: boolean;
    lineClasses?: string;
    groupClasses?: string;
    class?: typeof LegendItem;
    pmType?: string;
    legendData?: LegendData;
}

interface LegendContext {
    valueTimeGraph?: ValueTimeGraph;
    allowedZoomOnClick?: boolean;
    [key: string]: unknown;
}

class LegendItem {
    declare type: string;

    title?: string;

    declare clickable: boolean;

    declare hidden: boolean;

    declare available: boolean;

    header?: boolean;

    information?: string;

    line?: boolean;

    lineClasses?: string;

    groupClasses?: string;

    pmType?: string;

    declare legendData: LegendData;

    constructor(options: LegendItemOptions) {
        if (!Object.keys(options).includes('type')) {
            throw new Error('Legend item must have a type property.');
        }

        // Assign options + defaults:
        Object.assign(this, {
            clickable: true,
            // Hidden specifies whether or not to draw the data in the graph.
            hidden: false,
            // Available indicates whether the legend item is available.
            available: true,
            ...options,
        });
    }

    isDisabled(): boolean {
        return this.hidden;
    }

    onClick(): void {
        this.legendData.setHidden(this.type, !this.hidden);
    }
}

class PMLegendItem extends LegendItem {
    ASK_BEFORE_ZOOM_QUESTION = gettext('ASK_BEFORE_ZOOM_QUESTION');

    isDisabled(): boolean {
        return !this.available || this.hidden;
    }

    getMinutes(): number {
        // Turns pm101440m into 1440.
        return parseInt(this.type.substring(this.pmType!.length, this.type.length - 1), 10);
    }

    onClick(): void {
        // This is the onClick handler for PM10 legend items. When executed and
        // the PM10 data is not available it zooms in on the value-time graph to
        // the level where the data for that legend item should be available. If
        // the data is available, the visibility of the data will be toggled.
        if (this.available) {
            super.onClick();
            return;
        }

        if (
            !this.legendData.context.allowedZoomOnClick &&
            // eslint-disable-next-line no-alert, no-restricted-globals
            !confirm(this.ASK_BEFORE_ZOOM_QUESTION)
        ) {
            // User does not want to zoom.
            return;
        }

        this.legendData.context.allowedZoomOnClick = true;

        const graph = this.legendData.context?.valueTimeGraph;

        // In case it we have already been clicked without the valueTimeGraph
        // being initialized or available in our context.
        if (!graph) {
            return;
        }

        const extraLoadedByQuery = 1 + scrollTrigger * 4;
        const maxBuckets = 1000;
        const minutesNeededInView = (this.getMinutes() * maxBuckets) / extraLoadedByQuery;

        (graph.zoom as ValueTimeGraphZoomHelper).zoomToScale(minutesNeededInView * getMinuteDays());
    }
}

const minuteUnit = gettext('min');
const hourUnit = gettext('hour');

const types: LegendItemOptions[] = [
    {
        type: 'x',
        title: 'X',
    },
    {
        type: 'y',
        title: 'Y',
    },
    {
        type: 'z',
        title: 'Z',
    },
    {
        type: 'vector',
        title: 'PVS',
    },
    {
        type: 'pm10-information',
        title: '',
        clickable: false,
        header: true,
        information: gettext('PM10_ZOOM_I_TJE'),
    },
    {
        type: 'pm2p5-information',
        title: '',
        clickable: false,
        header: true,
        information: gettext('PM10_ZOOM_I_TJE').replaceAll('PM x', 'PM2.5'),
    },
    ...['10', '2.5'].flatMap((pmType) => [
        {
            type: `pm${pmType.replace('.', 'p')}1m`,
            title: `PM${pmType === '10' ? ' x' : pmType} 1${minuteUnit}`,
            class: PMLegendItem,
            pmType: `pm${pmType.replace('.', 'p')}`,
        },
        {
            type: `pm${pmType.replace('.', 'p')}15m`,
            title: `PM${pmType === '10' ? ' x' : pmType} 15${minuteUnit}`,
            class: PMLegendItem,
            pmType: `pm${pmType.replace('.', 'p')}`,
        },
        {
            type: `pm${pmType.replace('.', 'p')}60m`,
            title: `PM${pmType === '10' ? ' x' : pmType} 1${hourUnit}`,
            class: PMLegendItem,
            pmType: `pm${pmType.replace('.', 'p')}`,
        },
        // Not implemented for now.
        // {
        //     type: 'pm' + pmType.replace('.', 'p') + '600m',
        //     title: `PM${pmType} 10${gettext('hour')}`,
        //     class: PMLegendItem,
        //     pmType: 'pm' + pmType.replace('.', 'p'),
        // },
        {
            type: `pm${pmType.replace('.', 'p')}1440m`,
            title: `PM${pmType === '10' ? ' x' : pmType} 24${hourUnit}`,
            class: PMLegendItem,
            pmType: `pm${pmType.replace('.', 'p')}`,
        },
    ]),
    {
        type: 'm',
        title: gettext('Event'),
    },
];

export class LegendData {
    updated$: Subject<void>;

    context: LegendContext;

    entries: LegendItem[];

    visibleTypes$: Observable<string[]>;

    hiddenTypes$: Observable<string[]>;

    constructor() {
        this.updated$ = new Subject<void>();

        // Shared context that can for example be used in onClick handlers
        // of legend items.
        this.context = {};

        this.entries = types.map((options) => {
            const LegendItemClass = options.class ?? LegendItem;
            return new LegendItemClass({
                ...options,
                legendData: this,
            });
        });

        this.visibleTypes$ = this.updated$.pipe(
            startWith(null),
            map(() => this.getVisibleTypes())
        );

        this.hiddenTypes$ = this.updated$.pipe(
            startWith(null),
            map(() => this.getHiddenTypes())
        );
    }

    getEntries(): LegendItem[] {
        return this.entries;
    }

    setHidden(type: string, hidden: boolean): void {
        const entry = this.findType(type);
        entry.hidden = hidden;
        this.updated$.next();
    }

    findType(type: string): LegendItem {
        const result = this.getEntries().find((entry) => entry.type === type);
        if (!result) {
            throw new Error(`Legend item with type "${type}" not found.`);
        }
        return result;
    }

    removeType(type: string): void {
        const entry = this.findType(type);
        remove(this.entries, entry);
    }

    removeAllExceptType(type: string): void {
        this.entries
            .filter((entry) => entry.type !== type)
            .forEach((entry) => remove(this.entries, entry));
    }

    getHiddenTypes(): string[] {
        return this.getEntries()
            .filter((entry) => entry.hidden)
            .map((entry) => entry.type);
    }

    getVisibleTypes(): string[] {
        return this.getEntries()
            .filter((entry) => !entry.hidden)
            .map((entry) => entry.type);
    }

    isVisible(type: string): string | undefined {
        return this.getVisibleTypes().find((t) => t === type);
    }
}

dustGraphDataTypesWithoutPM10.forEach((dustMetaDataType) => {
    types.push({
        type: dustMetaDataType,
        title: getDataType(dustMetaDataType).title,
    });
});

['x', 'y', 'z'].forEach((axis) => {
    types.push({
        type: `vper-${axis}`,
        title: `${axis.toUpperCase()} Vper`,
        line: true,
        lineClasses: `vperline ${axis}`,
        groupClasses: `${axis}`,
    });
});

interface SVGTextElementWithTippy extends SVGTextElement {
    _tippy: TippyInstance;
}

function getLegendEntries$(
    excludeTypes$: Observable<string[]>,
    legendData: LegendData
): Observable<LegendItem[]> {
    return combineLatest([
        excludeTypes$,
        // Trigger the first render before the legend data emits an update.
        legendData.updated$.pipe(startWith(null)),
    ]).pipe(
        map(([excludeTypes]) =>
            legendData
                .getEntries()
                .filter((entry) => collectionNotIncludes(excludeTypes, entry.type))
        )
    );
}

abstract class BaseLegendRenderer {
    graph: ValueTimeGraph;

    excludeTypes$: Observable<string[]>;

    legendData: LegendData;

    renderSubscription!: Subscription;

    constructor(
        graph: ValueTimeGraph,
        excludeTypes$: Observable<string[]>,
        legendData: LegendData
    ) {
        this.graph = graph;
        this.excludeTypes$ = excludeTypes$;
        this.legendData = legendData;
    }

    protected startRendering(): void {
        this.renderSubscription = getLegendEntries$(this.excludeTypes$, this.legendData).subscribe(
            (entries) => {
                this.render(entries);
            }
        );
    }

    protected abstract removeElement(): void;

    abstract render(entries: LegendItem[]): void;

    destroy(): void {
        this.renderSubscription.unsubscribe();
        this.removeElement();
    }

    static create$<Renderer extends BaseLegendRenderer, Args extends unknown[]>(
        this: new (...args: Args) => Renderer,
        ...args: Args
    ): Observable<Renderer> {
        return new Observable((subscriber) => {
            const legendRenderer = new this(...args);

            subscriber.next(legendRenderer);

            return () => {
                legendRenderer.destroy();
            };
        });
    }
}

export class LegendRenderer extends BaseLegendRenderer {
    drawLine: Line<[number, number]>;

    backgroundMargin: number;

    identifierRectSize: number;

    x: number;

    y: number;

    element: Selection<SVGGElement, unknown, null, undefined>;

    constructor(
        graph: ValueTimeGraph,
        excludeTypes$: Observable<string[]>,
        legendData: LegendData
    ) {
        super(graph, excludeTypes$, legendData);

        this.drawLine = line();

        const legendWidth = 200;
        this.backgroundMargin = 6;
        this.identifierRectSize = 8;
        this.x = this.graph.graphWidth - legendWidth;
        this.y = 12;

        // Add the legend to the graph element of the graph.
        this.element = graph.graph.append('g').attr('class', 'legend');

        this.element
            .append('rect')
            .attr('x', this.x - 2)
            .attr('y', this.y - this.backgroundMargin)
            .attr('width', legendWidth)
            .attr('class', 'legend-background');

        this.startRendering();
    }

    protected removeElement(): void {
        this.element.remove();
    }

    render(entries: LegendItem[]): void {
        const itemOffset = 20; // px

        // Calculate top offsets:
        let lastOffset = this.y;
        const topOffsets: number[] = [];

        entries.forEach(() => {
            topOffsets.push(lastOffset);

            lastOffset += itemOffset;
        });

        const backgroundHeight =
            last(topOffsets)! - this.y + this.identifierRectSize + this.backgroundMargin * 2;

        this.element.selectAll('.legend-background').attr('height', backgroundHeight);

        this.element
            .selectAll<SVGGElement, LegendItem>('g')
            .data(entries, (d) => d.type)
            .join(
                (enter) => {
                    const group = enter
                        .append('g')
                        .attr('class', (d) => {
                            let classes = d.type;

                            if (d.groupClasses) {
                                classes += ` ${d.groupClasses}`;
                            }
                            return classes;
                        })
                        .classed('clickable', (d) => d.clickable)
                        .on('click', (_event, d) => {
                            if (d.clickable) {
                                d.onClick();
                            }
                        });

                    // Render an indicator if this legend entry is not an header.
                    group
                        .filter((d) => !d.header)
                        .append('rect')
                        .attr('x', this.x)
                        .attr('width', this.identifierRectSize)
                        .attr('height', this.identifierRectSize)
                        .classed('line-background', matchesProperty('line', true));

                    group
                        .filter(matchesProperty('line', true))
                        .append('path')
                        .attr('class', (d) => d.lineClasses ?? '');

                    // Add an 'i-tje' if this item has information text.
                    group
                        .filter((d) => !isNil(d.information))
                        .append('text')
                        // 16 = icon width, 2 = small margin.
                        .attr('x', this.graph.graphWidth - (16 + 2))
                        .attr('font-family', 'FontAwesome')
                        .attr('class', 'information-icon')
                        .text('\uf05a')
                        .each((d, i, nodes) => {
                            tippy(nodes[i], {
                                content: d.information!,
                                theme: 'omnidots-white-shadow',
                            });
                        });

                    // Add text if title property is filled.
                    group
                        .filter((d) => !!d.title)
                        .append('text')
                        .attr('x', this.x + 15)
                        .text((d) => d.title!);

                    return group;
                },
                (update) => update,
                (exit) => {
                    exit.filter((d) => !isNil(d.information))
                        .select<SVGTextElementWithTippy>('text.information-icon')
                        .each((_d, i, nodes) => {
                            // Destroy Tippy.
                            nodes[i]._tippy.destroy();
                        });

                    return exit.remove();
                }
            )
            // Update locations on every render.
            .call((selection) => {
                selection.select('rect').attr('y', (_d, index) => topOffsets[index]);

                selection.select('path').attr('d', (_d, index) => {
                    const middleOfRect = topOffsets[index] + 4;
                    return this.drawLine([
                        [
                            this.x, // x
                            middleOfRect, // y
                        ],
                        [
                            this.x + this.identifierRectSize, // x
                            middleOfRect, // y
                        ],
                    ]);
                });

                selection
                    .select('text')
                    .attr('y', (_d, index) => topOffsets[index] + this.identifierRectSize);
            })
            .classed('disabled', (d) => d.isDisabled());
    }
}

function getHeaderLegendItemClassName(item: LegendItem): string {
    return [
        'graph-header-legend-item',
        item.type,
        item.groupClasses,
        item.clickable ? 'clickable' : null,
        item.isDisabled() ? 'disabled' : null,
        item.available ? null : 'unavailable',
    ]
        .filter(Boolean)
        .join(' ');
}

/**
 * Renders the legacy legend in the graph header instead of on top of plotted data.
 * Report Builder graphs use this renderer, while existing graph pages keep the SVG overlay.
 */
export class HeaderLegendRenderer extends BaseLegendRenderer {
    element: Selection<HTMLDivElement, unknown, null, undefined>;

    constructor(
        graph: ValueTimeGraph,
        excludeTypes$: Observable<string[]>,
        legendData: LegendData
    ) {
        super(graph, excludeTypes$, legendData);

        if (!graph.headerLegendContainer) {
            throw new Error('Header legend placement requires a header legend container.');
        }

        this.element = select(graph.headerLegendContainer)
            .append('div')
            .attr('class', 'graph-header-legend-items');

        this.startRendering();
    }

    render(entries: LegendItem[]): void {
        const items = this.element
            .selectAll<HTMLButtonElement, LegendItem>('.graph-header-legend-item')
            .data(entries, (item) => item.type)
            .join(
                (enter) => {
                    const item = enter
                        .append('button')
                        .attr('type', 'button')
                        .on('click', (_event, legendItem) => {
                            if (legendItem.clickable) {
                                legendItem.onClick();
                            }
                        });

                    const indicator = item
                        .append('svg')
                        .attr('class', 'graph-header-legend-indicator')
                        .attr('viewBox', '0 0 20 8')
                        .attr('aria-hidden', 'true');
                    indicator.append('rect').attr('width', 8).attr('height', 8);
                    indicator
                        .append('line')
                        .attr('x1', 0)
                        .attr('x2', 20)
                        .attr('y1', 4)
                        .attr('y2', 4);
                    item.append('span').attr('class', 'graph-header-legend-label');
                    return item;
                },
                (update) => update,
                (exit) => exit.remove()
            )
            .attr('class', getHeaderLegendItemClassName)
            .attr('title', (item) => item.title || item.information || null)
            .attr('aria-label', (item) => item.title || item.information || null)
            .attr('aria-pressed', (item) => (item.clickable ? String(!item.isDisabled()) : null))
            .attr('tabindex', (item) => (item.clickable ? null : -1));

        items
            .select('.graph-header-legend-indicator')
            .attr('viewBox', (item) => (item.line ? '0 0 20 8' : '0 0 8 8'))
            .classed('graph-header-legend-indicator--line', (item) => !!item.line)
            .classed('graph-header-legend-indicator--hidden', (item) => !!item.header);
        items
            .select('.graph-header-legend-indicator rect')
            .attr('class', (item) => `bargraph ${item.type}`)
            .classed('graph-header-legend-shape--hidden', (item) => !!item.line);
        items
            .select('.graph-header-legend-indicator line')
            .attr('class', (item) => item.lineClasses ?? `linegraph ${item.type}`)
            .classed('graph-header-legend-shape--hidden', (item) => !item.line);

        items
            .select('.graph-header-legend-label')
            .text((item) => item.title || (item.information ? 'ⓘ' : ''));
    }

    protected removeElement(): void {
        this.element.remove();
    }
}
